// src/components/helpdesk/HelpdeskAIPanel.js
// ENHANCED: Enterprise-Grade AI Command Center (Ollama RAG Integration)
// Features: AbortControllers, Memoization, Glassmorphism, Typing Effects, IoT Context

import React, { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, VStack, HStack, Text, Icon, Badge, Button, 
  Alert, AlertIcon, AlertTitle, AlertDescription,
  useColorModeValue, Tooltip, IconButton, Spinner,
  Collapse, ScaleFade, SimpleGrid, Progress, Divider,
  SkeletonText, SkeletonCircle, Flex, useToast
} from '@chakra-ui/react';
import { 
  MdLightbulbOutline, MdRefresh, MdClose, MdArrowForward, 
  MdErrorOutline, MdCheckCircle, MdWarning, MdInfo,
  MdAccessTime, MdPriorityHigh, MdAssignment, MdTimer,
  MdTrendingUp, MdGroup, MdBarChart, MdSensors, MdAutoAwesome,
  // --- UPDATE (AI Insights v3): icons used by the server engine + feedback buttons
  MdThumbUp, MdThumbDown, MdTrendingDown, MdTimerOff, MdPhoneInTalk, MdBubbleChart, MdReportProblem,
  MdLocationOff, MdHelpOutline, MdModelTraining, MdSentimentDissatisfied
  // --- END UPDATE
} from 'react-icons/md';
// --- UPDATE (Phone AI Helpdesk): use the shared API base + token helper (was REACT_APP_API_BASE_URL +
// localStorage only, which broke with sessionStorage logins and on deployments without that env var)
import { authenticatedFetch } from '../../services/api';
// --- END UPDATE

// ============================================================================
// MOTION VARIANTS & UTILS
// ============================================================================
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const containerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, staggerChildren: 0.1 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 300, damping: 25 } }
};

const iconMap = {
  MdErrorOutline, MdAccessTime, MdPriorityHigh, MdAssignment,
  MdInfo, MdCheckCircle, MdTrendingUp, MdGroup, MdTimer, MdBarChart, MdWarning,
  // --- UPDATE (AI Insights v3)
  MdTrendingDown, MdTimerOff, MdPhoneInTalk, MdBubbleChart, MdReportProblem, MdLocationOff,
  MdHelpOutline, MdModelTraining, MdSentimentDissatisfied, MdAutoAwesome,
  // --- END UPDATE
};

// ============================================================================
// AI UX COMPONENT: PULSING INDICATOR
// Shows when Ollama is actively processing RAG context
// ============================================================================
const AIPulseIndicator = memo(() => (
  <HStack spacing={2} p={2} bg="purple.50" borderRadius="full" px={4} border="1px solid" borderColor="purple.100">
    <MotionBox
      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      w="8px" h="8px" bg="purple.500" borderRadius="full"
    />
    <Text fontSize="xs" fontWeight="700" color="purple.700" textTransform="uppercase" letterSpacing="wider">
      Ollama is analyzing IoT sensors...
    </Text>
  </HStack>
));

// ============================================================================
// AI UX COMPONENT: TYPEWRITER TEXT
// Simulates a streaming AI response for premium UX
// ============================================================================
const TypewriterText = memo(({ text, speed = 20 }) => {
  const [displayedText, setDisplayedText] = useState('');
  
  useEffect(() => {
    setDisplayedText('');
    if (!text) return;
    
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText((prev) => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
      }
    }, speed);
    
    return () => clearInterval(timer);
  }, [text, speed]);

  return <Text fontSize="sm" color="gray.700" lineHeight="1.6" fontWeight="500">{displayedText}</Text>;
});

// ============================================================================
// COMPONENT: GLOBAL INSIGHT CARD (Memoized for Performance)
// ============================================================================
const GlobalInsightCard = memo(({ insight, status, colorScheme, onAction }) => {
  // --- UPDATE (AI Insights v3): helpful / not helpful -> shared feedback loop
  const [rated, setRated] = useState(0);
  const rate = (rating) => { setRated(rating); onAction && onAction({ actionType: 'rate', actionPayload: { insight, rating } }); };
  // --- END UPDATE
  const IconComponent = iconMap[insight.icon] || MdInfo;
  const alertBg = useColorModeValue(`${colorScheme}.50`, 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  
  return (
    <MotionBox variants={cardVariants}>
      <Alert
        status={status} variant="subtle" flexDirection="column" alignItems="start"
        justifyContent="space-between" borderRadius="xl" height="100%"
        borderLeftWidth="4px" borderLeftColor={`${colorScheme}.400`}
        bg={alertBg} boxShadow="sm" p={4} position="relative" overflow="hidden"
        transition="all 0.2s" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
      >
        <Box w="full" position="relative" zIndex={1}>
          <HStack mb={3} justify="space-between" align="start">
            <HStack spacing={2}>
              <Icon as={IconComponent} boxSize={5} color={`${colorScheme}.600`} />
              <Badge colorScheme={colorScheme} fontSize="10px" fontWeight="800" px={2} borderRadius="full">
                {insight.severity?.toUpperCase()}
              </Badge>
              {/* --- UPDATE (AI Insights v3): where the insight came from */}
              {insight.source === 'server' && (
                <Tooltip label={insight.engine ? `FlowSense AI engine (${insight.engine})` : 'FlowSense AI engine — live data'}>
                  <Badge colorScheme="purple" variant="outline" fontSize="9px" borderRadius="full">AI</Badge>
                </Tooltip>
              )}
            </HStack>
            {insight.id && insight.key !== 'hd.briefing' && (
              <HStack spacing={0}>
                <Tooltip label="Helpful">
                  <IconButton size="xs" variant="ghost" aria-label="Helpful" icon={<MdThumbUp />} isDisabled={rated !== 0} color={rated === 1 ? 'green.500' : undefined} onClick={() => rate(1)} />
                </Tooltip>
                <Tooltip label="Not helpful (5 votes hide this kind of insight)">
                  <IconButton size="xs" variant="ghost" aria-label="Not helpful" icon={<MdThumbDown />} isDisabled={rated !== 0} color={rated === -1 ? 'red.500' : undefined} onClick={() => rate(-1)} />
                </Tooltip>
              </HStack>
            )}
            {/* --- END UPDATE */}
          </HStack>
          
          <AlertTitle fontSize="sm" fontWeight="800" mb={2} color={textColor}>
            {insight.title}
          </AlertTitle>
          <AlertDescription fontSize="xs" color={mutedColor} lineHeight="tall" mb={3}>
            {insight.message}
          </AlertDescription>

          {/* --- UPDATE (AI Insights v3): the records behind the insight */}
          {Array.isArray(insight.evidence) && insight.evidence.length > 0 && (
            <VStack align="start" spacing={0} mb={2}>
              {insight.evidence.slice(0, 3).map((e) => (
                <Text key={String(e.id)} fontSize="10px" color={mutedColor} noOfLines={1}>
                  • {e.label || e.id}{e.hoursLate !== undefined ? ` — ${e.hoursLate} h late` : ''}{e.callers !== undefined ? ` — ${e.callers} callers` : ''}
                </Text>
              ))}
            </VStack>
          )}
          {/* --- END UPDATE */}
          {insight.metrics?.percentage && (
            <Box mt={3} mb={3}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" fontWeight="600" color={mutedColor}>Impact</Text>
                <Text fontSize="xs" fontWeight="700" color={textColor}>{insight.metrics.percentage}%</Text>
              </HStack>
              <Progress value={parseFloat(insight.metrics.percentage)} size="sm" colorScheme={colorScheme} borderRadius="full" />
            </Box>
          )}
        </Box>
        
        {insight.action && (
          <Button
            size="sm" mt={3} rightIcon={<MdArrowForward />} w="full" borderRadius="lg"
            colorScheme={status === 'error' ? 'red' : 'purple'} variant="outline" bg="white"
            onClick={() => onAction(insight)} _hover={{ transform: 'translateX(2px)' }}
          >
            {insight.action}
          </Button>
        )}
      </Alert>
    </MotionBox>
  );
});

// ============================================================================
// COMPONENT: TICKET RAG ANALYSIS PANEL (Ollama Integration)
// ============================================================================
const TicketAnalysisPanel = ({ ticket, colorModeVals }) => {
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    if (!ticket) return;

    // Enterprise Performance: Abort previous pending requests if dispatcher clicks a new ticket quickly
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const analyzeTicket = async () => {
      setIsAnalyzing(true);
      setError(null);
      setAnalysis(null);

      try {
        // --- UPDATE: authenticatedFetch returns parsed JSON and throws on HTTP errors
        const data = await authenticatedFetch('/ai/helpdesk-insight', {
          method: 'POST',
          body: JSON.stringify({
            ticketId: ticket.id,
            description: ticket.description,
            lat: ticket.latitude || ticket.target_lat || null,
            lng: ticket.longitude || ticket.target_lng || null
          }),
          signal: abortControllerRef.current.signal
        });
        if (!data) throw new Error('Ollama Engine Unreachable');
        // --- END UPDATE
        setAnalysis(data.insight); // 🔥 FIX: Using data.insight mapping
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError("Failed to generate AI context. The local LLaMA model may be offline.");
          toast({ title: "AI Offline", status: "warning", duration: 3000, isClosable: true });
        }
      } finally {
        setIsAnalyzing(false);
      }
    };

    analyzeTicket();

    // Cleanup abort controller on unmount
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [ticket, toast]);

  return (
    <Box bg="white" p={5} borderRadius="xl" borderWidth="1px" borderColor="purple.100" boxShadow="sm">
      <HStack mb={4} justify="space-between">
        <HStack>
          <Box p={2} bg="purple.100" borderRadius="lg"><Icon as={MdAutoAwesome} color="purple.600" boxSize={5} /></Box>
          <Text fontWeight="800" fontSize="md" color="gray.800">Ollama Ticket Triage</Text>
        </HStack>
        <Badge colorScheme="purple" variant="outline" px={3} py={1} borderRadius="full">
          Ticket #{ticket.id}
        </Badge>
      </HStack>

      <Divider mb={4} />

      {isAnalyzing ? (
        <VStack align="start" spacing={4} py={4}>
          <AIPulseIndicator />
          <SkeletonText mt="4" noOfLines={3} spacing="4" skeletonHeight="2" w="full" />
        </VStack>
      ) : error ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      ) : analysis ? (
        <VStack align="start" spacing={4}>
          <HStack bg="blue.50" p={2} borderRadius="md" w="full">
            <Icon as={MdSensors} color="blue.500" />
            <Text fontSize="xs" fontWeight="600" color="blue.700">Live IoT Context Applied</Text>
          </HStack>
          <Box bg="gray.50" p={4} borderRadius="lg" w="full" borderLeft="3px solid" borderColor="purple.400">
            <TypewriterText text={analysis} speed={15} />
          </Box>
        </VStack>
      ) : (
        <Text fontSize="sm" color="gray.500">Awaiting ticket data...</Text>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN EXPORT: HELPDESK AI PANEL
// ============================================================================
const HelpdeskAIPanel = ({ 
  insights = [], 
  selectedTicket = null, // 🔥 NEW: Passes the active ticket from the dispatcher
  isLoading = false, 
  onRefresh, 
  onClose, 
  onAction, 
  isVisible = true 
}) => {
  // THEME HOOKS
  const cardBg = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.9)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const headerGradient = useColorModeValue('linear(to-r, purple.500, blue.600)', 'linear(to-r, purple.400, blue.500)');
  const statBg = useColorModeValue('purple.50', 'purple.900');

  const colorModeVals = { cardBg, borderColor, textColor, mutedColor, headerGradient, statBg };

  const criticalCount = insights.filter(i => i.severity === 'critical').length;
  const highCount = insights.filter(i => i.severity === 'high').length;

  const statusMap = { warning: 'warning', success: 'success', info: 'info', critical: 'error' };
  const severityColorMap = { critical: 'red', high: 'orange', medium: 'yellow', low: 'blue' };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <MotionBox key="ai-panel" variants={containerVariants} initial="hidden" animate="visible" exit="exit">
        <Collapse in={isVisible} animateOpacity>
          <Box
            bg={cardBg} p={6} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}
            boxShadow="2xl" mb={6} position="relative" overflow="hidden" backdropFilter="blur(10px)"
          >
            {/* Glassmorphism Background Gradients */}
            <Box position="absolute" top="-100px" right="-100px" w="300px" h="300px" bgGradient="radial(circle, purple.100, transparent)" opacity={0.4} filter="blur(40px)" zIndex={0} pointerEvents="none" />
            
            <Box position="absolute" top={0} left={0} right={0} height="4px" bgGradient={headerGradient} />

            <VStack align="stretch" spacing={5} position="relative" zIndex={1}>
              {/* HEADER */}
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Box p={2.5} bgGradient={headerGradient} borderRadius="xl" boxShadow="md">
                    <Icon as={MdAutoAwesome} color="white" boxSize={6} />
                  </Box>
                  <Box>
                    <Text fontWeight="800" fontSize="xl" color={textColor} lineHeight="1.2">
                      {selectedTicket ? "Copilot Ticket Analysis" : "Global System Insights"}
                    </Text>
                    {!selectedTicket && (
                      <HStack spacing={2} mt={1}>
                        <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={3} fontSize="xs">{insights.length} Active</Badge>
                        {criticalCount > 0 && <Badge colorScheme="red" borderRadius="full" px={3} fontSize="xs"><Icon as={MdErrorOutline} mr={1}/>{criticalCount} Critical</Badge>}
                      </HStack>
                    )}
                  </Box>
                </HStack>

                <HStack>
                  <Tooltip label="Regenerate AI Analysis" hasArrow>
                    <IconButton icon={<MdRefresh />} variant="ghost" onClick={onRefresh} isLoading={isLoading} aria-label="Refresh AI" borderRadius="xl" size="md" />
                  </Tooltip>
                  <IconButton icon={<MdClose />} variant="ghost" size="md" onClick={onClose} aria-label="Close Panel" borderRadius="xl" />
                </HStack>
              </HStack>

              {/* DYNAMIC CONTENT ROUTER */}
              {selectedTicket ? (
                // MODE 1: OLLAMA RAG TICKET ANALYSIS
                <TicketAnalysisPanel ticket={selectedTicket} colorModeVals={colorModeVals} />
              ) : (
                // MODE 2: GLOBAL SYSTEM INSIGHTS (Python ML Engine / Rules)
                <>
                  {isLoading ? (
                    <HStack justify="center" py={10}>
                      <Spinner color="purple.500" thickness="3px" size="lg" />
                      <VStack spacing={1} align="start" ml={4}>
                        <Text color={textColor} fontSize="md" fontWeight="600">Analyzing global telemetry...</Text>
                        <Text color={mutedColor} fontSize="sm">Processing ML baselines</Text>
                      </VStack>
                    </HStack>
                  ) : insights.length === 0 ? (
                    <Alert status="success" variant="subtle" borderRadius="xl">
                      <AlertIcon as={MdCheckCircle} boxSize={5} />
                      <Box>
                        <AlertTitle fontSize="sm" fontWeight="700">System Healthy</AlertTitle>
                        <AlertDescription fontSize="xs">No anomalies detected across the FlowSense grid.</AlertDescription>
                      </Box>
                    </Alert>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <AnimatePresence>
                        {insights.map((insight, idx) => (
                          <GlobalInsightCard
                            key={`${insight.title}-${idx}`}
                            insight={insight}
                            status={statusMap[insight.severity] || 'info'}
                            colorScheme={severityColorMap[insight.severity] || 'blue'}
                            onAction={onAction}
                          />
                        ))}
                      </AnimatePresence>
                    </SimpleGrid>
                  )}
                </>
              )}

              {/* FOOTER */}
              <Divider />
              <HStack justify="space-between" fontSize="xs" color={mutedColor}>
                <Text fontWeight="600">
                  {selectedTicket ? "Powered by local LLaMA 3" : "Powered by FlowSense Python ML"} • Updated: {new Date().toLocaleTimeString()}
                </Text>
                <HStack><Icon as={MdInfo} boxSize={3} /><Text>Enterprise Intelligence Mesh</Text></HStack>
              </HStack>

            </VStack>
          </Box>
        </Collapse>
      </MotionBox>
    </AnimatePresence>
  );
};

export default memo(HelpdeskAIPanel);